// src/components/common/Table.tsx
import React from "react";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
    Button, Checkbox, Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui";
import { cn } from "@/lib/utils";

export type Column<T> = {
    key: keyof T | string;
    label: string;
    render?: (value: T[keyof T] | undefined, row: T) => React.ReactNode;
    sortable?: boolean;
};

interface TableProps<T> {
    columns: Column<T>[];
    data: T[];
    selectedItems?: Set<string>;
    onSelectAll?: () => void;
    onSelectItem?: (item: T) => void;
    onViewItem?: (item: T) => void;
    onEditItem?: (item: T) => void;
    onDeleteItem?: (item: T) => void;
    getId: (item: T) => string;
    className?: string;
    expandedRowId?: string | null;
    renderExpandedRow?: (item: T) => React.ReactNode;
    rowClassName?: (item: T) => string;
    sortConfig?: { key: keyof T | null; direction: 'asc' | 'desc' };
    onSort?: (key: keyof T) => void;
}

export function DataTable<T extends object>({
    columns,
    data,
    selectedItems,
    onSelectAll,
    onSelectItem,
    onViewItem,
    onEditItem,
    onDeleteItem,
    getId,
    className,
    expandedRowId,
    renderExpandedRow,
    rowClassName,
    sortConfig,
    onSort,
}: TableProps<T>) {
    const { t } = useTranslation();
    const hasSelection = selectedItems !== undefined && onSelectAll !== undefined && onSelectItem !== undefined;

    return (
        <div className={cn("border-b overflow-hidden", className)}>
            <Table>
                <TableHeader>
                    <TableRow>
                        {hasSelection && (
                            <TableHead className="w-[50px]">
                                <Checkbox
                                    checked={selectedItems.size === data.length}
                                    onCheckedChange={onSelectAll}
                                    aria-label={t("common.selectAll")}
                                />
                            </TableHead>
                        )}
                        {columns.map((column) => (
                            <TableHead
                                key={String(column.key)}
                                className={cn(
                                    column.sortable && "cursor-pointer hover:bg-gray-100",
                                    sortConfig?.key === column.key && "bg-gray-100"
                                )}
                                onClick={() => column.sortable && onSort?.(column.key as keyof T)}
                            >
                                <div className="flex items-center gap-2">
                                    {column.label}
                                    {column.sortable && sortConfig?.key === column.key && (
                                        <span className="text-gray-500">
                                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </div>
                            </TableHead>
                        ))}
                        {(onViewItem || onEditItem || onDeleteItem) && (
                            <TableHead className="w-[100px]">{t("common.actions")}</TableHead>
                        )}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.map((item) => (
                        <React.Fragment key={getId(item)}>
                            <TableRow className={rowClassName?.(item)}>
                                {hasSelection && (
                                    <TableCell>
                                        <Checkbox
                                            checked={selectedItems.has(getId(item))}
                                            onCheckedChange={() => onSelectItem(item)}
                                            aria-label={t("common.selectItem")}
                                        />
                                    </TableCell>
                                )}
                                {columns.map((column) => (
                                    <TableCell key={String(column.key)}>
                                        {column.render
                                            ? column.render(column.key in item ? item[column.key as keyof T] : undefined, item)
                                            : String(column.key in item ? item[column.key as keyof T] : '')}
                                    </TableCell>
                                ))}
                                {(onViewItem || onEditItem || onDeleteItem) && (
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {onViewItem && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => onViewItem(item)}
                                                >
                                                    <Eye className="h-4 w-4" strokeWidth={1.5} />
                                                </Button>
                                            )}
                                            {onEditItem && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => onEditItem(item)}
                                                >
                                                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                                                </Button>
                                            )}
                                            {onDeleteItem && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => onDeleteItem(item)}
                                                >
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                )}
                            </TableRow>
                            {expandedRowId === getId(item) && renderExpandedRow && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + (hasSelection ? 1 : 0) + (onViewItem || onEditItem || onDeleteItem ? 1 : 0)}>
                                        {renderExpandedRow(item)}
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
} 